const PREMIUM_ROUTE_PATH = "/menu/subscription";
const ACTIVE_CLASS = "vg-premium-route-transition-active";
const SNAPSHOT_CLASS = "vg-premium-route-snapshot";
const SNAPSHOT_LEAVING_CLASS = "vg-premium-route-snapshot--leaving";
const SNAPSHOT_DURATION_MS = 860;

let activeSnapshot = null;
let cleanupTimer = null;

const canUseDom = () =>
  typeof window !== "undefined" &&
  typeof document !== "undefined" &&
  Boolean(document.body);

const prefersReducedMotion = () => {
  if (!canUseDom() || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

const resolvePathname = (url) => {
  if (!canUseDom() || url === undefined || url === null) return "";

  try {
    return new URL(String(url), window.location.href).pathname;
  } catch {
    return "";
  }
};

const isSubscriptionTarget = (url) => resolvePathname(url) === PREMIUM_ROUTE_PATH;

const removeActiveSnapshot = () => {
  if (cleanupTimer) {
    window.clearTimeout(cleanupTimer);
    cleanupTimer = null;
  }

  if (activeSnapshot) {
    activeSnapshot.remove();
    activeSnapshot = null;
  }

  if (canUseDom()) {
    document.documentElement.classList.remove(ACTIVE_CLASS);
  }
};

export const beginPremiumRouteTransition = () => {
  if (!canUseDom() || prefersReducedMotion()) return;

  const root = document.getElementById("root");
  if (!root) return;

  removeActiveSnapshot();

  const snapshot = document.createElement("div");
  snapshot.className = SNAPSHOT_CLASS;
  snapshot.setAttribute("aria-hidden", "true");

  const clone = root.cloneNode(true);
  clone.classList.add("vg-premium-route-snapshot__clone");
  snapshot.appendChild(clone);

  activeSnapshot = snapshot;
  document.documentElement.classList.add(ACTIVE_CLASS);
  document.body.appendChild(snapshot);

  window.requestAnimationFrame(() => {
    if (snapshot.isConnected) {
      snapshot.classList.add(SNAPSHOT_LEAVING_CLASS);
    }
  });

  const finish = () => {
    if (activeSnapshot === snapshot) {
      removeActiveSnapshot();
    } else {
      snapshot.remove();
    }
  };

  snapshot.addEventListener("animationend", finish, { once: true });
  cleanupTimer = window.setTimeout(finish, SNAPSHOT_DURATION_MS);
};

export const installPremiumRouteTransitionHistoryPatch = () => {
  if (!canUseDom() || !window.history) return () => {};

  const history = window.history;
  const previousPatch = window.__vgPremiumRouteTransitionPatch;
  if (previousPatch?.restore) {
    previousPatch.restore();
  }

  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  const maybeBeginTransition = (url) => {
    if (
      isSubscriptionTarget(url) &&
      window.location.pathname !== PREMIUM_ROUTE_PATH
    ) {
      beginPremiumRouteTransition();
    }
  };

  function patchedPushState(state, title, url) {
    maybeBeginTransition(url);
    return originalPushState.apply(this, arguments);
  }

  function patchedReplaceState(state, title, url) {
    maybeBeginTransition(url);
    return originalReplaceState.apply(this, arguments);
  }

  history.pushState = patchedPushState;
  history.replaceState = patchedReplaceState;

  const patch = {
    restore: () => {
      if (history.pushState === patchedPushState) {
        history.pushState = originalPushState;
      }
      if (history.replaceState === patchedReplaceState) {
        history.replaceState = originalReplaceState;
      }
      if (window.__vgPremiumRouteTransitionPatch === patch) {
        delete window.__vgPremiumRouteTransitionPatch;
      }
    },
  };

  window.__vgPremiumRouteTransitionPatch = patch;
  return patch.restore;
};
